import MobileDetect from "mobile-detect";
import queryString from "query-string";
import { debugTools } from "../../utils/debugTools";

import {
  castContentIdString,
  fetchApi,
  postApiPaywall,
  postFetchApi,
} from "./util";

// Função para aplicar timeout
const timeout = (ms: number) => {
  return new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), ms));
};

// Função que faz a requisição com timeout, limitando o tempo total a 2 segundos
const fetchWithTimeout = async (url: string, payload: any, cid: string, maxRetries = 3, timeoutMs = 3000) => {
  let attempt = 0;
  let result = null;

  while (attempt < maxRetries) {
    try {
      const controller = new AbortController();
      const signal = controller.signal;

      const fetchTimeout = setTimeout(() => {
        controller.abort();
      }, timeoutMs);

      let response;

      if (payload.email === undefined || !payload.email) {
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          signal
        });
      } else if (!cid.includes('1.')) {
        response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json'
          },
          signal
        });
      }

      clearTimeout(fetchTimeout);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      result = await response.json();
      console.log(`[DEBUG] Sucesso: `, result);

      if (result) return result;
    } catch (e) {
      debugTools.log(`[DEBUG] Tentativa falhou: `, e);
      console.log(`[DEBUG] Tentativa para o id falhou: `, cid);
      console.log(`[DEBUG] Tentativa falhou: `, e);
    }
    attempt++;
  }
  throw new Error(`Failed to fetch after ${maxRetries} attempts`);
};

const handlePromiseResult = (result: Array<PromiseSettledResult<any>>): any[] => {
  const fulfilledSlots: any[] = [];
  result.forEach(
    (res) =>
      res.status === "fulfilled" &&
      res.value.forEach((content: any) => fulfilledSlots.push(content))
  );
  return fulfilledSlots;
};

const loadByContentList = async (data: Record<string, any>, session: any, absoluteUrl = "") => {
  const contentKeys = Object.keys(data);
  const contentList: string[] = [];
  const promisesList: Array<Promise<any>> = [];
  const md = new MobileDetect(window.navigator.userAgent);

  const deviceType = md.match(
    /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
  )
    ? "MOBILE"
    : "DESKTOP";

  const payload = {
    email: session?.emailUsuario,
    product: process.env.NEXT_PUBLIC_PAYWALL_PRODUCT,
    deviceType,
    sessionId: session?.id,
  };

  contentKeys.forEach((key) =>
    data[key].forEach((cid: any) => cid && contentList.push(castContentIdString(cid)))
  );

  for (const cid of contentList) {
    debugTools.log(
      "[DEBUG] (loadByContentList) postFetchApi: ",
      `${absoluteUrl}/siteApi/contentId/${cid}`,
      payload
    );
    promisesList.push(fetchWithTimeout(`${absoluteUrl}/siteApi/contentId/${cid}`, payload, cid));
  }

  try {
    const fetchResult = await Promise.allSettled(promisesList);
    const fulfilledSlots = handlePromiseResult(fetchResult);
    debugTools.log(
      "[DEBUG] (loadByContentList) fulfilledSlots: ",
      fulfilledSlots
    );
    return fulfilledSlots;
  } catch (e) {
    debugTools.log(
      "[DEBUG] (loadByContentList) [Fetch error :: loadByContentList]: ",
      e
    );
    return [];
  }
};

const loadImageByContentList = async (contentList: string[]) => {
  const promisesArray: Array<Promise<Response>> = contentList
    .map((image_url) => image_url ? fetch(image_url) : null)
    .filter(Boolean) as Array<Promise<Response>>;

  let data: any[] = [];
  const promiseResult = await Promise.allSettled(promisesArray);

  promiseResult.forEach((item) => {
    if (item.status === "fulfilled") {
      item?.value && data.push(item?.value);
    }
  });

  return data;
};

// Função para carregar dados por CID de forma não bloqueante
const loadByCid = async (cid: string, absoluteUrl = "", session: any, device: string) => {
  const payload = {
    email: session?.emailUsuario,
    product: process.env.NEXT_PUBLIC_PAYWALL_PRODUCT,
    deviceType: device,
    polopolyContent: {
      contentId: cid,
      accessLevel: session?.nivelAcesso,
    },
    sessionId: session?.id,
  };

  debugTools.log(
    "[DEBUG] (loadByCid) postApiPaywall: ",
    `${absoluteUrl}/siteApi/contentId/${cid}`,
    payload
  );

  return postApiPaywall(`${absoluteUrl}/siteApi/contentId/${cid}`, payload);
};

// Função para carregar filtros com callbacks, sem bloquear o fluxo
const loadByCidFilter = (params: any, callback: (p: any) => void) => {
  const parsedData = `${queryString.stringify(params)}`;
  const url = `/siteApi/filter/${params.policyId}?${parsedData}`;

  fetchApi(url)
    .then((response) => callback(response))
    .catch((e) =>
      console.log("[Fetch Error :: loadDataByCidFilter]: ", e)
    );
};

export {
  loadByContentList,
  loadByCidFilter,
  loadByCid,
  loadImageByContentList,
};